import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { OEPanelGammaLeg } from "./oe-panel-gamma-leg.model";
import { OEGammaLegType } from "../enums/oe-gamma-leg-type";
import { OEPanelBaseModel } from "./oe-panel-base.model";
import { BigNumber, OrderType } from "shared/cubiq-model-multi-asset";
import * as internal from "stream";
import { BfxInputControlStateEnum } from "src/app/bfx-interface-controls/models/bfx-interface-controls.enums";
import { BfxRequestPriceModel } from "app/bfx-interface-controls/models/bfx-interface-controls.models";
import { GammaUtils } from "../oe-gamma-utilities";

export class OEPanelGammaViewModel extends OEPanelBaseModel {
  public static ALERT_RED: string = "#D12A36";
  private static NORMAL: string = "#677489";

  public gammaLegsAbove: Array<OEPanelGammaLeg> = [];
  public gammaLegsBelow: Array<OEPanelGammaLeg> = [];
  public rungsAbove: number = this.gammaLegsAbove.length;
  public rungsBelow: number = this.gammaLegsBelow.length;
  public hasError: boolean = false;
  public displayMessage: String =
    "Calculated gamma legs cannot have a negative rate, please review the Reference Mid, Delta and Rungs Below criteria in the order form.";
  public edit: boolean = false;
  public customiseLabel: string = "Customise";
  public externalState: BfxInputControlStateEnum = BfxInputControlStateEnum.VALID;
  public productCode: string;
  public inverted: boolean;

  public dirHeaderLabel: string;
  public amountHeaderLabel: string;
  public rateHeaderLabel: string;
  public deltaHeaderLabel: string;
  public buyLabel: string;
  public sellLabel: string;
  public pipsLabel: string;
  public effectiveBase: String;
  public effectiveNonBase: String;

  public customizeMode: boolean;

  private _gammaMedLeg: OEPanelGammaLeg;
  private _isCustom: Boolean = false;
  private _delta: BigNumber;
  private _orderType: CubiqTS.OrderType = CubiqTS.OrderType.LIMIT_TP;
  private _midRate: Array<BfxRequestPriceModel> = new Array();

  public get midRate(): Array<BfxRequestPriceModel> {
    return this._midRate;
  }
  public set midRate(value: Array<BfxRequestPriceModel>) {
    this._midRate = value;
  }
  private _amount: BigNumber;

  public get amount(): BigNumber {
    return this._amount;
  }
  public set amount(value: BigNumber) {
    this._amount = value;
  }

  public get orderType(): CubiqTS.OrderType {
    return this._orderType;
  }
  public set orderType(value: CubiqTS.OrderType) {
    this._orderType = value;
  }

  public get delta(): BigNumber {
    return this._delta;
  }
  public set delta(value: BigNumber) {
    this._delta = value;
  }

  public get isCustom(): Boolean {
    return this._isCustom;
  }
  public set isCustom(value: Boolean) {
    this._isCustom = value;
  }

  public get gammaMedLeg(): OEPanelGammaLeg {
    return this._gammaMedLeg;
  }
  public set gammaMedLeg(value: OEPanelGammaLeg) {
    this._gammaMedLeg = value;
  }

  public getDirection(above: boolean): CubiqTS.OrderIntent {
    if (above) {
      if (
        (this._orderType == OrderType.LIMIT_TP && this.effectiveBase == this.fixedCcy.fixedCcy) ||
        (this._orderType == OrderType.LIMIT_SL && this.effectiveNonBase === this.fixedCcy.fixedCcy)
      ) {
        return CubiqTS.OrderIntent.SELL;
      } else {
        return CubiqTS.OrderIntent.BUY;
      }
    } else {
      if (
        (this._orderType == OrderType.LIMIT_TP && this.effectiveBase == this.fixedCcy.fixedCcy) ||
        (this._orderType == OrderType.LIMIT_SL && this.effectiveNonBase == this.fixedCcy.fixedCcy)
      ) {
        return CubiqTS.OrderIntent.BUY;
      } else {
        return CubiqTS.OrderIntent.SELL;
      }
    }
  }

  public regenerateGammaLegModel(): void {
    this.gammaLegsAbove = [];
    this.gammaLegsBelow = [];
    if (this._midRate == null || this.midRate.length == 0) {
      return;
    }
    if (
      !this.currencyPair ||
      !this._midRate[0] ||
      this.orderType == null ||
      !this.fixedCcy ||
      this.rungsAbove + this.rungsBelow <= 0 ||
      (this._delta != null && this._delta.toNumber() <= 0)
    ) {
      let rate: BigNumber = null;

      if (this._midRate.length > 0 && this._midRate[0].value != null) {
        rate = this.midRate[0].value;
      }

      return this.temporaryMidDisplay(this.currencyPair, this.fixedCcy, rate);
    }
    if (
      this.currencyPair != null &&
      this.currencyPair.model != null &&
      this.currencyPair.product == null
    ) {
      this.currencyPair.product = this.currencyPair.model.base + this.currencyPair.model.nonBase;
    }

    let entryDecs: number = GammaUtils.getPairEntryPrecision(
      this.currencyPair.model,
      this.currencyPair.inverted,
    );
    let spotPrecision: number =
      entryDecs -
      (entryDecs -
        GammaUtils.getSpotPrecision(this.currencyPair.model, this.currencyPair.inverted));

    let basicIncrement: string = (this._delta.toNumber() * Math.pow(10, -spotPrecision)).toFixed(
      entryDecs,
    );

    var deltaIncrement: BigNumber = GammaUtils.getPipsIncrementPart(
      this.currencyPair.model,
      this.currencyPair.inverted,
      this._delta,
    );
    var gammaLegs: Array<OEPanelGammaLeg> = [];
    var leg: OEPanelGammaLeg;
    var currentDelta: BigNumber;
    var currentRate: BfxRequestPriceModel;

    var midRate: BigNumber;
    var i: number;
    var midRateStr: string = "";

    if (this._midRate[0].textValue) {
      midRateStr = this._midRate[0].textValue;
      this._midRate[0].value = new BigNumber(midRateStr).decimalPlaces(entryDecs);
      midRate = new BigNumber(midRateStr);
    }

    currentRate = new BfxRequestPriceModel();
    currentRate.value = new BigNumber(midRate.toNumber()).decimalPlaces(entryDecs);
    currentRate.textValue = currentRate.value.toFixed(entryDecs);

    if (this.rungsAbove > 0) {
      for (i = i = 0; i < this.rungsAbove; i++) {
        leg = new OEPanelGammaLeg();
        leg.viewerModel = this;
        leg.legType = OEGammaLegType.ABOVE;
        leg.ccyPair = this.currencyPair;
        leg.fixedCcy = this.fixedCcy;
        leg.buyXLabel = this.buyLabel;
        leg.sellXLabel = this.sellLabel;
        leg.pipsXLabel = this.pipsLabel;
        leg.delta = this.delta;
        leg.minDelta = new BigNumber(2); //todo
        currentDelta = new BigNumber(deltaIncrement);

        var midRateStr: string = this._midRate[0].value.toFixed(entryDecs);
        var midRate: BigNumber = new BigNumber(midRateStr);
        var roundedCurrentDelta: BigNumber = currentDelta.decimalPlaces(entryDecs);
        currentRate.value = currentRate.value.plus(roundedCurrentDelta);
        currentRate.value.toString(); //.8815

        leg.rate = new BigNumber(currentRate.value.toFixed(entryDecs));

        if (
          (this._orderType == OrderType.LIMIT_TP && this.effectiveBase == this.fixedCcy.fixedCcy) ||
          (this._orderType == OrderType.LIMIT_SL &&
            this.effectiveNonBase === this.fixedCcy.fixedCcy)
        ) {
          leg.direction = CubiqTS.OrderIntent.SELL;
        } else {
          leg.direction = CubiqTS.OrderIntent.BUY;
        }

        leg.legType = OEGammaLegType.ABOVE;
        leg.amount = this._amount;
        leg.legId = i + 1;
        this.gammaLegsAbove.push(leg);
        //this.updateMinOrMaxAndDeltaAbove(leg);
      }
    } else {
      this.gammaLegsAbove = [];
    }
    // add the mid rate
    leg = new OEPanelGammaLeg();
    leg.buyXLabel = this.buyLabel;
    leg.sellXLabel = this.sellLabel;
    leg.pipsXLabel = this.pipsLabel;
    leg.ccyPair = this.currencyPair;
    leg.legId = 0;
    leg.fixedCcy = this.fixedCcy;

    leg.rate = this._midRate[0].value;
    leg.delta = this.delta;
    leg.amount = this.amount;
    leg.legType = OEGammaLegType.MID;
    gammaLegs.push(leg);
    // add the legs below the mid leg
    currentRate = new BfxRequestPriceModel();
    currentRate.value = new BigNumber(midRate.toNumber()).decimalPlaces(entryDecs); //.87930
    currentRate.textValue = currentRate.value.toFixed(entryDecs);

    let basicIncrementBelow: string = (
      this._delta.toNumber() * Math.pow(10, -spotPrecision)
    ).toFixed(entryDecs);
    var deltaIncrement: BigNumber = new BigNumber(basicIncrementBelow).decimalPlaces(entryDecs);
    if (this.rungsBelow > 0) {
      for (i = 1; i <= this.rungsBelow; i++) {
        leg = new OEPanelGammaLeg();
        leg.delta = this.delta;
        leg.buyXLabel = this.buyLabel;
        leg.sellXLabel = this.sellLabel;
        leg.pipsXLabel = this.pipsLabel;
        leg.ccyPair = this.currencyPair;
        leg.fixedCcy = this.fixedCcy;
        leg.minDelta = new BigNumber(2); //minimumgammadetlapips
        var item: string = (i * deltaIncrement.toNumber()).toFixed(entryDecs);
        currentDelta = new BigNumber(item).decimalPlaces(entryDecs);
        currentDelta.toString();
        leg.rate = currentRate.value.minus(currentDelta);

        if (leg.rate.toNumber() <= 0) {
          this.hasError = true;
          leg.hasError = true;
          leg.borderColor = OEPanelGammaViewModel.ALERT_RED;
          leg.externalState = BfxInputControlStateEnum.INVALID;
          this.temporaryMidDisplay(
            this.currencyPair,
            this.fixedCcy,
            this._midRate[0].value,
            "Calculated gamma legs cannot have a negative rate, please review the Reference Mid, Delta and Rungs Below criteria in the order form.",
          );
        } else {
          leg.borderColor = OEPanelGammaViewModel.NORMAL;
          leg.hasError = false;
          this.hasError = false;
        }

        if (
          (this._orderType == OrderType.LIMIT_TP && this.effectiveBase == this.fixedCcy.fixedCcy) ||
          (this._orderType == OrderType.LIMIT_SL && this.effectiveNonBase == this.fixedCcy.fixedCcy)
        ) {
          leg.direction = CubiqTS.OrderIntent.BUY;
        } else {
          leg.direction = CubiqTS.OrderIntent.SELL;
        }

        leg.amount = this._amount;
        leg.legType = OEGammaLegType.BELOW;
        leg.legId = i * -1;
        this.gammaLegsBelow.push(leg);
        //this.updateMinOrMaxAndDeltaBelow(leg);
      }
    } else {
      this.gammaLegsBelow = [];
      // No gamma legs below no negative rates so we set the flag to false
      this.hasError = false;
    }

    this.gammaLegsAbove.sort(function (a, b) {
      if (a.rate > b.rate) return -1;
      if (a.rate < b.rate) return 1;
      return 0;
    });
  }

  public sort(above: boolean): void {
    if (above) {
      this.gammaLegsAbove.sort(function (a, b) {
        let first: BigNumber = a.rate;
        let second: BigNumber = b.rate;
        if (first > second) {
          return -1;
        }
        if (first < second) {
          return 1;
        }
        return 0;
      });
    } else {
      this.gammaLegsBelow.sort(function (a, b) {
        let first: BigNumber = a.rate;
        let second: BigNumber = b.rate;
        if (first > second) {
          return -1;
        }
        if (first < second) {
          return 1;
        }
        return 0;
      });
    }
  }

  public recalulateDeltaAbove(
    leg: OEPanelGammaLeg,
    aboveLeg: OEPanelGammaLeg,
    belowLeg: OEPanelGammaLeg,
    midLegRate: BigNumber,
  ): void {
    let entryDecs: number = GammaUtils.getPairEntryPrecision(
      this.currencyPair.model,
      this.currencyPair.inverted,
    );
    let spotPrecision: number =
      entryDecs -
      (entryDecs -
        GammaUtils.getSpotPrecision(this.currencyPair.model, this.currencyPair.inverted));
    let fraction: BigNumber = null;
    let multiplier: BigNumber = new BigNumber(Math.pow(10, spotPrecision));
    let delta: CubiqTS.BigNumber = null; //aboveLeg.rate.minus(newLeg.rate);
    let belowLegRate: BigNumber;
    if (belowLeg == null) {
      //belowLeg = midLeg;
      belowLegRate = midLegRate;
    } else {
      belowLegRate = belowLeg.rate;
    }
    fraction = leg.rate.minus(belowLegRate); //0.00007
    leg.delta = fraction.times(multiplier);
  }

  public recalulateDeltaBelow(
    leg: OEPanelGammaLeg,
    aboveLeg: OEPanelGammaLeg,
    belowLeg: OEPanelGammaLeg,
    midLegRate: BigNumber,
  ): void {
    let entryDecs: number = GammaUtils.getPairEntryPrecision(
      this.currencyPair.model,
      this.currencyPair.inverted,
    );
    let spotPrecision: number =
      entryDecs -
      (entryDecs -
        GammaUtils.getSpotPrecision(this.currencyPair.model, this.currencyPair.inverted));
    let fraction: BigNumber = null;
    let multiplier: BigNumber = new BigNumber(Math.pow(10, spotPrecision));
    let aboveLegRate: BigNumber;
    if (aboveLeg == null) {
      aboveLegRate = midLegRate;
    } else {
      aboveLegRate = aboveLeg.rate;
    }

    fraction = aboveLegRate.minus(leg.rate).absoluteValue();
    leg.delta = fraction.times(multiplier);
  }

  private temporaryMidDisplay(
    _ccyPair: any,
    fixed: any,
    midRate: BigNumber,
    errorString: String = null,
  ): void {
    var midLeg: OEPanelGammaLeg = new OEPanelGammaLeg();
    midLeg.ccyPair = _ccyPair;
    midLeg.fixedCcy = fixed;
    midLeg.rate = midRate;
    midLeg.legType = OEGammaLegType.MID;
    midLeg.errorString = errorString;
    this._gammaMedLeg = midLeg;
  }

  public cloneViewModel(): OEPanelGammaViewModel {
    let dest: OEPanelGammaViewModel = new OEPanelGammaViewModel();

    Object.assign(dest, this);

    dest._amount = this._amount;
    dest._delta = this._delta;
    dest._isCustom = this._isCustom;
    dest._orderType = this._orderType;
    dest.customizeMode = this.customizeMode;

    if (this._gammaMedLeg) {
      dest._gammaMedLeg = this._gammaMedLeg.cloneGammaLeg();
    }

    if (Array.isArray(this._midRate) && this._midRate.length == 1) {
      let mid = this._midRate[0];
      dest._midRate = [new BfxRequestPriceModel(mid.value, mid.textValue)];
    }

    dest.gammaLegsAbove = this.cloneLegs(this.gammaLegsAbove);
    dest.gammaLegsBelow = this.cloneLegs(this.gammaLegsBelow);

    return dest;
  }

  private cloneLegs(source: OEPanelGammaLeg[]): OEPanelGammaLeg[] {
    let legs: OEPanelGammaLeg[] = null;

    if (Array.isArray(source)) {
      legs = source.map((r) => r.cloneGammaLeg());

      legs.forEach((r, i) => {
        if (i > 0) {
          r.aboveLeg = legs[i - 1];
        }

        if (i < legs.length - 1) {
          r.belowLeg = legs[i + 1];
        }
      });

      return legs;
    }

    return legs;
  }
}
